/** Dependencies **/
import React from 'react';
import { 
  VictoryChart, 
  VictoryLine, 
  VictoryAxis, 
  VictoryGroup,
  VictoryScatter,
  createContainer
} from 'victory';

/** Redux **/
import { useSelector } from 'react-redux';

/** Components **/
import DataVizTooltipPerfAvgPos from './../../DataVizTooltips/DataVizTooltipPerfAvgPos';

/** Helpers **/
import { rewriteTrendObjectData } from './../../../../../helpers/barometer/charts.js';
import { orderObjectKeysWithPattern } from './../../../../../helpers/functions';

function DataVizPerfAvgPos( props ) 
{
  /** Get props **/
  const grpDomainDisplay = props.grpDomainDisplay;
  const compareDisplay = props.compareDisplay;

  /** Get state from redux store */
  const instanceConfig = useSelector( state => state.config.value );  
  const togglers = useSelector( state => state.togglers.value ); 

  /** Create Custom Victory Container for cursor and voronoi **/
  const VictoryCursorVoronoiContainer = createContainer( "voronoi", "cursor" );

  /** Get config values **/
  const grpDomains = instanceConfig.groups;

  /** Init datas for chart **/
  let currentPerfTrend = {};
  let comparePerfTrend = {};

  /** Rewrite array datas **/
  if( 
    props.currentPerfTrend
    && props.currentPerfTrend.avgPositionsGrpDomains
    && Object.keys( props.currentPerfTrend.avgPositionsGrpDomains ).length > 0
  ){
    // Rewrite Current Perf Trend datas
    currentPerfTrend = rewriteTrendObjectData( 
      props.currentPerfTrend.avgPositionsGrpDomains,
      togglers['radio-view-option'] ? 'month' : 'week'
    );

    // sort current datas with grp domains labels
    currentPerfTrend = orderObjectKeysWithPattern( currentPerfTrend, grpDomains.map( grpDomain => grpDomain.value ) );

    if( 
      props.comparePerfTrend
      && props.comparePerfTrend.avgPositionsGrpDomains
      && Object.keys( props.comparePerfTrend.avgPositionsGrpDomains ).length > 0
    ){
      // Rewrite Compare Perf Trend datas
      comparePerfTrend = rewriteTrendObjectData( 
        props.comparePerfTrend.avgPositionsGrpDomains,
        togglers['radio-view-option'] ? 'month' : 'week'
      );

      // sort compare datas with grp domains labels
      comparePerfTrend = orderObjectKeysWithPattern( comparePerfTrend, grpDomains.map( grpDomain => grpDomain.value ) );
    }
  };

  /** Get if all displayed values = 0 to set default dependant axis values */
  // get current sum values
  let valuesSumDisplay = Object.keys( grpDomainDisplay ).filter( grp => grpDomainDisplay[grp] ).map( grp => 
    currentPerfTrend[grp] ? currentPerfTrend[grp].reduce( ( a, b ) => a + b.y, 0 ) : 0 
  ).reduce( (a, b) => a + b, 0 );

  // get compare sum values
  valuesSumDisplay += Object.keys( grpDomainDisplay ).filter( grp => grpDomainDisplay[grp] ).map( grp => 
    comparePerfTrend[grp] ? comparePerfTrend[grp].reduce( ( a, b ) => a + b.y, 0 ) : 0
  ).reduce( (a, b) => a + b, 0 );

  return (
    <React.Fragment>
      {( 
        Object.keys( currentPerfTrend ).length > 0 
        && Object.keys( grpDomainDisplay ).filter( grp => grpDomainDisplay[grp] ).length > 0
      ) ?
        <VictoryChart
          width={1300}
          height={250}
          padding={{ top: 10, bottom: 30, left: 50, right: 20 }}
          minDomain={{y: 0}}
          maxDomain={{y: 101}}
          containerComponent={
            <VictoryCursorVoronoiContainer 
              voronoiBlacklist={["currentLine", "compareLine"]}
              voronoiDimension="x"
              cursorDimension="x"
              labels={ () => ' ' } // space value needed to display label component
              labelComponent={ <DataVizTooltipPerfAvgPos /> }  
            />
          }
        >
          <VictoryAxis
            tickValues={currentPerfTrend[Object.keys( currentPerfTrend )[0]].map( elem => elem.tickValue )}
            style={{
              tickLabels: {
                fontSize: 6.5,
                fontFamily: "Roboto",
                fontWeight: "bold",
                fill: "#626262",
                angle: -45       
              },
              grid: {
                stroke: '#f5f2f2', 
                strokeWidth: 1
              }
            }}
            orientation='top'
          />
          <VictoryAxis dependentAxis 
            offsetX={50}
            tickValues={valuesSumDisplay === 0 ? [0] : []}
            style={{
              tickLabels: {
                fontSize: 16,
                fontFamily: "Roboto",
                fontWeight: "300",
                fill: "#707070"
              },
              grid: {
                stroke: '#D8D8D8', 
                strokeWidth: 0.5
              }
            }}
            invertAxis={true}
          />
          {Object.keys( currentPerfTrend ).map( ( key, index ) => 
            grpDomainDisplay[key] ?
              <VictoryGroup 
                key={index}
                data={currentPerfTrend[key]}
              >
                <VictoryScatter
                  name={"currentScatter" + index}
                  style={{
                    data: {
                      fill: grpDomains.filter( grp => grp.value === key )[0].currentColor
                    }
                  }}
                  size={({ active }) => active ? 5 : 3 } 
                />
                <VictoryLine            
                  name="currentLine"
                  style={{ 
                    data: { 
                      stroke: grpDomains.filter( grp => grp.value === key )[0].currentColor
                    } 
                  }}
                />                
              </VictoryGroup>
            : false                
          )}
          {compareDisplay ?
            Object.keys( comparePerfTrend ).map( ( key, index ) =>
              grpDomainDisplay[key] ?
                <VictoryGroup 
                  key={index}
                  data={comparePerfTrend[key]}
                >
                  <VictoryScatter
                    name={"compareScatter" + index}
                    style={{
                      data: {
                        fill: grpDomains.filter( grp => grp.value === key )[0].currentColor
                      }
                    }}
                    size={({ active }) => active ? 5 : 3 } 
                  />
                  <VictoryLine
                    name="compareLine"
                    style={{ 
                      data: { 
                        stroke: grpDomains.filter( grp => grp.value === key )[0].currentColor,
                        strokeDasharray: 3,
                        strokeWidth: 1
                      }
                    }}
                  />                  
                </VictoryGroup>
              : false
            )
            : false
          }
        </VictoryChart>
        : false
      }
    </React.Fragment>
  )
}

export default DataVizPerfAvgPos;